// ─────────────────────────────────────────────────────────────────────────────
// WHERE A ROLE LANDS WHEN THE PAGE THEY ASKED FOR IS NOT THEIRS.
//
// 🚨 THE ALTERNATIVE WAS A PAGE THAT RENDERED AND THEN FAILED. Jesse: "If they
// do not/should not have access, they should not be able to view or see the
// page at all. It creates confusion when they see a page, but can't navigate to
// it or get an error message if they are able to view it." The symptom was a
// Partner Success rep opening the admin Dashboard and meeting an error box
// saying not everything had loaded — the server refusing, correctly, data the
// product had just offered them.
//
// 🔑 SO THIS IS AN EXPLANATION, NOT AN ERROR. It says the page is not part of
// their role rather than implying something is broken, and it offers the place
// they CAN work. A refusal with no way forward is the dead end that replaces a
// dead affordance, and it is not an improvement.
//
// ⚠️ AND IT IS NOT A SECURITY BOUNDARY. backend/constants/staffRoles.js is what
// denies the data, default-closed, independently of anything here. This decides
// what the product OFFERS.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStaffRole } from '../hooks/useStaffRole';
import { landingPathFor } from '../constants/staffNav';
import { ROLE_LABELS } from '../constants/staffRoleLabels';

export default function AdminNoAccess() {
  const { role } = useStaffRole();
  const navigate = useNavigate();
  const location = useLocation();
  const home = landingPathFor(role);
  // 🔑 WHERE THEY TRIED TO GO, carried in state by the guard. Naming it is the
  // difference between "that page is not yours" and a bare refusal that leaves
  // them wondering whether they mistyped something.
  const tried = (location.state && location.state.from) || null;
  const label = ROLE_LABELS[role] || role;

  return (
    <div style={{
      minHeight: '60vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
        That page is not part of your role
      </div>
      <div style={{ fontSize: 13.5, color: '#6b7280', textAlign: 'center', maxWidth: 420,
        lineHeight: 1.65 }}>
        {tried ? <>You asked for <code style={{ fontSize: 12.5 }}>{tried}</code>, which is not </>
          : 'That page is not '}
        available to the <strong>{label}</strong> role. Nothing is wrong and nothing is broken —
        it simply is not one of your surfaces, so we are not going to show you a version of it
        that cannot load.
      </div>
      <div style={{ fontSize: 12.5, color: '#9ca3af', textAlign: 'center', maxWidth: 420,
        marginTop: 10, lineHeight: 1.6 }}>
        If you need it, ask a Doorly admin to change your role rather than working around it.
      </div>
      {/* ⚠️ NOT "/admin/dashboard". That literal is how this problem started: it
          was every role's landing page and it is hidden from Partner Success. */}
      {home !== '/admin/no-access' && (
        <button type="button" onClick={() => navigate(home, { replace: true })}
          style={{ marginTop: 18, fontSize: 13, fontWeight: 700, color: '#fff',
            background: '#3a6ad9', border: 'none', borderRadius: 9,
            padding: '11px 20px', cursor: 'pointer' }}>
          Go where you can work
        </button>
      )}
    </div>
  );
}
